import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import '../assets/styles/PricingPage.css';
import '../assets/styles/EnhancedPricingCTA.css';
import '../assets/styles/EnhancedContactPage.css';

const PricingPage: React.FC = () => {
  const [activeMobilePlan, setActiveMobilePlan] = useState<string>('yearly');
  const trialSectionRef = useRef<HTMLDivElement>(null);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [totalLifetimeSeats, setTotalLifetimeSeats] = useState(20); // Default value
  const [availableLifetimeSeats, setAvailableLifetimeSeats] = useState(20); // Default value
  
  // Load configuration from public folder
  useEffect(() => {
    fetch('/siteConfig.json')
      .then(response => response.json())
      .then(data => {
        const total = data.pricing.lifetimeSeats.total;
        const rawAvailable = data.pricing.lifetimeSeats.available;
        
        // Apply validation logic
        const validatedAvailable = Math.min(
          Math.max(0, rawAvailable), // Ensure it's not negative
          total // Ensure it doesn't exceed total
        );
        
        setTotalLifetimeSeats(total);
        setAvailableLifetimeSeats(validatedAvailable);
        setConfigLoaded(true);
      })
      .catch(error => {
        console.error('Error loading site configuration:', error);
        setConfigLoaded(true); // Set to true even on error to avoid UI blocking
      });
  }, []);
  
  // Check if lifetime seats are sold out
  const isLifetimeSoldOut = availableLifetimeSeats <= 0;
  
  // Button styles based on availability
  const lifetimeButtonClass = isLifetimeSoldOut 
    ? "btn btn-primary btn-sm disabled" 
    : "btn btn-primary btn-sm";
  
  const lifetimeMobileButtonClass = isLifetimeSoldOut 
    ? "btn btn-primary btn-mobile disabled" 
    : "btn btn-primary btn-mobile";
  
  // Function to scroll to trial section
  const scrollToTrialSection = () => {
    trialSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  return (
    <div className="pricing-page">
      <Helmet>
        <title>Pricing Plans | Price on Rails | Translate Stocks</title>
        <meta 
          name="description" 
          content="Choose from flexible pricing options for Price on Rails, our AI-powered trading platform. Monthly, yearly, and lifetime plans available for traders of all levels."
        />
      </Helmet>

      <section className="pricing-hero">
        <div className="container">
          <div className="pricing-header">
            <h1>Studio PRO Pricing</h1>
            <h2>Invest with Confidence</h2>
            <h3 className="pricing-subheadline">
              <span className="highlight">Proven Statistical Edge + Dedicated AI Servers</span>
            </h3>
            
            <p className="pricing-description">
              Exclusive, private cloud infrastructure built specifically for your trading success. Each client gets a <span className="highlight">dedicated AI Server</span>!
            </p>
            
            <div className="key-advantages">
              <div className="advantage-pill">
                <div className="advantage-icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM9 17H7V10H9V17ZM13 17H11V7H13V17ZM17 17H15V13H17V17Z" fill="#2563eb"/>
                  </svg>
                </div>
                <span>78-83% Win Rate</span>
              </div>
              <div className="advantage-pill">
                <div className="advantage-icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 6H20V8H4V6ZM4 11H20V13H4V11ZM4 16H20V18H4V16Z" fill="#2563eb"/>
                  </svg>
                </div>
                <span>Multi-Server Environment</span>
              </div>
              <div className="advantage-pill">
                <div className="advantage-icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="#2563eb"/>
                  </svg>
                </div>
                <span>White-Glove Support</span>
              </div>
            </div>
            
            <button onClick={scrollToTrialSection} className="btn btn-trial">
              Try for 15 Days
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 4L10.59 5.41L16.17 11H4V13H16.17L10.59 18.59L12 20L20 12L12 4Z" fill="currentColor"/>
              </svg>
            </button>
          </div>
        </div>
      </section>

      <section className="pricing-plans">
        <div className="container">
          <div className="pricing-table-container">
            {/* Mobile Plan Selector - hidden by default on desktop */}
            <div className="mobile-plan-selector">
              <button 
                className={activeMobilePlan === 'monthly' ? 'active' : ''} 
                onClick={() => setActiveMobilePlan('monthly')}
              >
                Monthly
              </button>
              <button 
                className={activeMobilePlan === 'yearly' ? 'active' : ''} 
                onClick={() => setActiveMobilePlan('yearly')}
              >
                Yearly
              </button>
              <button 
                className={activeMobilePlan === 'lifetime' ? 'active' : ''} 
                onClick={() => setActiveMobilePlan('lifetime')}
              >
                Lifetime
              </button>
            </div>

            {/* Mobile Price Display - hidden by default on desktop */}
            <div className="mobile-price-display">
              {activeMobilePlan === 'monthly' && (
                <div className="mobile-price-card">
                  <div className="mobile-plan-name">MONTHLY</div>
                  <div className="mobile-plan-price">$2,512<span className="cents">.78</span></div>
                  <div className="mobile-plan-billing">per month</div>
                  <a href="https://pay.translatestocks.com/b/9AQ6roa6dgKa5jO00H" className="btn btn-primary btn-mobile">Start Now</a>
                </div>
              )}

              {activeMobilePlan === 'yearly' && (
                <div className="mobile-price-card popular">
                  <div className="mobile-popular-tag">Most Popular</div>
                  <div className="mobile-plan-name">YEARLY</div>
                  <div className="mobile-plan-price">$25,127<span className="cents">.88</span></div>
                  <div className="mobile-plan-billing">per year</div>
                  <div className="mobile-plan-savings">16.6% savings</div>
                  <a href="https://pay.translatestocks.com/b/6oE9DA9291Pg7rWbJq" className="btn btn-primary btn-mobile">Go Annual</a>
                </div>
              )}

              {activeMobilePlan === 'lifetime' && (
                <div className="mobile-price-card">
                  <div className="mobile-plan-name">LIFETIME</div>
                  <div className="mobile-plan-price">$99,000</div>
                  <div className="mobile-plan-billing">one-time payment</div>
                  <div className="mobile-limited-availability">
                    <span className="counter-number">{availableLifetimeSeats} Seats only</span>
                  </div>
                  <a href={isLifetimeSoldOut ? "#" : "https://pay.translatestocks.com/b/14k030bahgKa9A48xh"} className={lifetimeMobileButtonClass}>
                    {isLifetimeSoldOut ? "Sold Out" : "Go Lifetime"}
                  </a>
                </div>
              )}
            </div>

            {/* Pricing Plan Headers */}
            <div className="pricing-table">
              <div className="pricing-table-header">
                <div className="feature-column">
                  <h3>Studio PRO Features</h3>
                </div>
                
                <div className={`plan-column monthly ${activeMobilePlan === 'monthly' ? 'mobile-active' : ''}`}>
                  <div className="plan-name">MONTHLY</div>                  
                  <div className="plan-price">$2,512<span className="cents">.78</span></div>
                  <div className="plan-billing">per month</div>    
                  <div className="plan-savings">&nbsp;</div>
                  <div className="plan-savings">&nbsp;</div>
                  <a href="https://pay.translatestocks.com/b/9AQ6roa6dgKa5jO00H" className="btn btn-primary btn-sm">Start Now</a>
                </div>
                        
                <div className={`plan-column popular yearly ${activeMobilePlan === 'yearly' ? 'mobile-active' : ''}`}>                  
                  <div className="plan-name">YEARLY</div>                  
                  <div className="plan-price">$25,127<span className="cents">.88</span></div>
                  <div className="plan-billing">per year</div>
                  <div className="plan-savings">Most Popular</div>
                  <div className="plan-savings">16.6% savings</div>                  
                  <a href="https://pay.translatestocks.com/b/6oE9DA9291Pg7rWbJq" className="btn btn-primary btn-sm">Go Annual</a>
                </div>
                
                <div className={`plan-column lifetime ${activeMobilePlan === 'lifetime' ? 'mobile-active' : ''}`}>
                  <div className="plan-name">LIFETIME</div>                  
                  <div className="plan-price">$99,000</div>
                  <div className="plan-billing">one-time payment</div>
                  <div className="limited-availability">
                    <span className="counter-number">{availableLifetimeSeats} Seats only</span>
                  </div>                  
                  <a href={isLifetimeSoldOut ? "#" : "https://pay.translatestocks.com/b/14k030bahgKa9A48xh"} className={lifetimeButtonClass}>
                    {isLifetimeSoldOut ? "Sold Out" : "Go Lifetime"}
                  </a>
                </div>
              </div>
              
              {/* Feature Rows */}
              <div className="pricing-table-body">
                <div className="feature-row flagship-feature">
                  <div className="feature-name">
                    <Link to="/price-on-rails" className="price-rails-link">
                      PRICE ON RAILS
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5.22 14.78a.75.75 0 001.06 0l7.22-7.22v5.69a.75.75 0 001.5 0v-7.5a.75.75 0 00-.75-.75h-7.5a.75.75 0 000 1.5h5.69l-7.22 7.22a.75.75 0 000 1.06z" clipRule="evenodd" />
                      </svg>
                    </Link>
                    <span className="flagship-description">Our proprietary AI-driven trading engine that powers all platform capabilities</span>
                  </div>
                  <div className={`plan-column monthly ${activeMobilePlan === 'monthly' ? 'mobile-active' : ''}`}>
                    <span className="flagship-checkmark">✓</span>
                  </div>
                  <div className={`plan-column yearly ${activeMobilePlan === 'yearly' ? 'mobile-active' : ''}`}>
                    <span className="flagship-checkmark">✓</span>
                  </div>
                  <div className={`plan-column lifetime ${activeMobilePlan === 'lifetime' ? 'mobile-active' : ''}`}>
                    <span className="flagship-checkmark">✓</span>
                  </div>
                </div>
                
                <div className="feature-row section-title">
                  <div className="feature-name">Core Platform Features</div>
                  <div className="plan-column monthly"></div>
                  <div className="plan-column yearly"></div>
                  <div className="plan-column lifetime"></div>
                </div>
                
                <div className="feature-row">
                  <div className="feature-name">Full Access to Web Dashboard Trading Signals</div>
                  <div className={`plan-column monthly ${activeMobilePlan === 'monthly' ? 'mobile-active' : ''}`}>
                    <span className="checkmark">✓</span>
                  </div>
                  <div className={`plan-column yearly ${activeMobilePlan === 'yearly' ? 'mobile-active' : ''}`}>
                    <span className="checkmark">✓</span>
                  </div>
                  <div className={`plan-column lifetime ${activeMobilePlan === 'lifetime' ? 'mobile-active' : ''}`}>
                    <span className="checkmark">✓</span>
                  </div>
                </div>
                
                <div className="feature-row">
                  <div className="feature-name">Full Access to Desktop Trading Platform</div>
                  <div className={`plan-column monthly ${activeMobilePlan === 'monthly' ? 'mobile-active' : ''}`}>
                    <span className="checkmark">✓</span>
                  </div>
                  <div className={`plan-column yearly ${activeMobilePlan === 'yearly' ? 'mobile-active' : ''}`}>
                    <span className="checkmark">✓</span>
                  </div>
                  <div className={`plan-column lifetime ${activeMobilePlan === 'lifetime' ? 'mobile-active' : ''}`}>
                    <span className="checkmark">✓</span>
                  </div>
                </div>
                
                <div className="feature-row">
                  <div className="feature-name">Fully Automated A.I. Analysis in real-time</div>
                  <div className={`plan-column monthly ${activeMobilePlan === 'monthly' ? 'mobile-active' : ''}`}>
                    <span className="checkmark">✓</span>
                  </div>
                  <div className={`plan-column yearly ${activeMobilePlan === 'yearly' ? 'mobile-active' : ''}`}>
                    <span className="checkmark">✓</span>
                  </div>
                  <div className={`plan-column lifetime ${activeMobilePlan === 'lifetime' ? 'mobile-active' : ''}`}>
                    <span className="checkmark">✓</span>
                  </div>
                </div>
                
                <div className="feature-row">
                  <div className="feature-name">10 Years of US Stocks Intraday Market Data</div>
                  <div className={`plan-column monthly ${activeMobilePlan === 'monthly' ? 'mobile-active' : ''}`}>
                    <span className="checkmark">✓</span>
                  </div>
                  <div className={`plan-column yearly ${activeMobilePlan === 'yearly' ? 'mobile-active' : ''}`}>
                    <span className="checkmark">✓</span>
                  </div>
                  <div className={`plan-column lifetime ${activeMobilePlan === 'lifetime' ? 'mobile-active' : ''}`}>
                    <span className="checkmark">✓</span>
                  </div>
                </div>
                
                <div className="feature-row">
                  <div className="feature-name">Programming Language TS-Script to build indicators</div>
                  <div className={`plan-column monthly ${activeMobilePlan === 'monthly' ? 'mobile-active' : ''}`}>
                    <span className="checkmark">✓</span>
                  </div>
                  <div className={`plan-column yearly ${activeMobilePlan === 'yearly' ? 'mobile-active' : ''}`}>
                    <span className="checkmark">✓</span>
                  </div>
                  <div className={`plan-column lifetime ${activeMobilePlan === 'lifetime' ? 'mobile-active' : ''}`}>
                    <span className="checkmark">✓</span>
                  </div>
                </div>
                                
                <div className="feature-row">
                  <div className="feature-name">Unlimited Backtests *</div>
                  <div className={`plan-column monthly ${activeMobilePlan === 'monthly' ? 'mobile-active' : ''}`}>
                    <span className="checkmark">✓</span>
                  </div>
                  <div className={`plan-column yearly ${activeMobilePlan === 'yearly' ? 'mobile-active' : ''}`}>
                    <span className="checkmark">✓</span>
                  </div>
                  <div className={`plan-column lifetime ${activeMobilePlan === 'lifetime' ? 'mobile-active' : ''}`}>
                    <span className="checkmark">✓</span>
                  </div>
                </div>
                
                <div className="feature-row">
                  <div className="feature-name">Unlimited Trading Robots *</div>
                  <div className={`plan-column monthly ${activeMobilePlan === 'monthly' ? 'mobile-active' : ''}`}>
                    <span className="checkmark">✓</span>
                  </div>
                  <div className={`plan-column yearly ${activeMobilePlan === 'yearly' ? 'mobile-active' : ''}`}>
                    <span className="checkmark">✓</span>
                  </div>
                  <div className={`plan-column lifetime ${activeMobilePlan === 'lifetime' ? 'mobile-active' : ''}`}>
                    <span className="checkmark">✓</span>
                  </div>
                </div>
                
                <div className="feature-row">
                  <div className="feature-name">Integration with Brokers for Order Routing</div>
                  <div className={`plan-column monthly ${activeMobilePlan === 'monthly' ? 'mobile-active' : ''}`}>
                    <span className="checkmark">✓</span>
                  </div>
                  <div className={`plan-column yearly ${activeMobilePlan === 'yearly' ? 'mobile-active' : ''}`}>
                    <span className="checkmark">✓</span>
                  </div>
                  <div className={`plan-column lifetime ${activeMobilePlan === 'lifetime' ? 'mobile-active' : ''}`}>
                    <span className="checkmark">✓</span>
                  </div>
                </div>
                
                <div className="feature-row">
                  <div className="feature-name">Access to a 16.6% Discount</div>
                  <div className={`plan-column monthly ${activeMobilePlan === 'monthly' ? 'mobile-active' : ''}`}>
                    <span className="no-mark">—</span>
                  </div>
                  <div className={`plan-column yearly ${activeMobilePlan === 'yearly' ? 'mobile-active' : ''}`}>
                    <span className="checkmark">✓</span>
                  </div>
                  <div className={`plan-column lifetime ${activeMobilePlan === 'lifetime' ? 'mobile-active' : ''}`}>
                    <span className="no-mark">—</span>
                  </div>
                </div>

                <div className="feature-row">
                  <div className="feature-name">Limited Edition Version (Only {availableLifetimeSeats} Seats Available)</div>
                  <div className={`plan-column monthly ${activeMobilePlan === 'monthly' ? 'mobile-active' : ''}`}>
                    <span className="no-mark">—</span>
                  </div>
                  <div className={`plan-column yearly ${activeMobilePlan === 'yearly' ? 'mobile-active' : ''}`}>
                    <span className="no-mark">—</span>
                  </div>
                  <div className={`plan-column lifetime ${activeMobilePlan === 'lifetime' ? 'mobile-active' : ''}`}>
                    <span className="checkmark">✓</span>
                  </div>
                </div>
                
                <div className="feature-row">
                  <div className="feature-name">Access to New Features in Preview Mode</div>
                  <div className={`plan-column monthly ${activeMobilePlan === 'monthly' ? 'mobile-active' : ''}`}>
                    <span className="no-mark">—</span>
                  </div>
                  <div className={`plan-column yearly ${activeMobilePlan === 'yearly' ? 'mobile-active' : ''}`}>
                    <span className="no-mark">—</span>
                  </div>
                  <div className={`plan-column lifetime ${activeMobilePlan === 'lifetime' ? 'mobile-active' : ''}`}>
                    <span className="checkmark">✓</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Trial Policy Card */}
            <div ref={trialSectionRef} id="trial-section" className="trial-policy-card">
              <h3>15-Day Trial Available</h3>
              <div className="trial-banner">
                <div className="trial-banner-icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="#2563eb"/>
                  </svg>
                </div>
                <div className="trial-banner-text">
                  <span className="trial-highlight">Try Price on Rails for just $150</span>
                  <p>Full access for 15 days with paper trading only</p>
                </div>
              </div>
              
              <div className="trial-benefits">
                <div className="trial-benefit-item">
                  <span className="benefit-icon">✓</span>
                  <span>$150 trial fee applied as credit if you purchase any plan</span>
                </div>
                <div className="trial-benefit-item">
                  <span className="benefit-icon">✓</span>
                  <span>Experience the core platform capabilities</span>
                </div>
                <div className="trial-benefit-item">
                  <span className="benefit-icon">✓</span>
                  <span>Paper trading only (no live trading during trial)</span>
                </div>
                <div className="trial-benefit-item">
                  <span className="benefit-icon">✓</span>
                  <span>Real-time quotes are always available on our systems.</span>
                </div>
                <div className="trial-benefit-item">
                  <span className="benefit-icon">✓</span>
                  <span>One trial per person, no exceptions</span>
                </div>
              </div>
              
              <div className="trial-cta">
                <a href="https://pay.translatestocks.com/b/6oEcPM0vD65wcMg3cW" className="btn btn-primary">Start Your Trial</a>
                <Link to="/trial-policy" className="view-policy-link">View detailed trial policy</Link>
              </div>
            </div>
            
            <div className="pricing-footnote">
              <p>* The number of features you can use simultaneously is limited only by your server's processing power and memory. Need more processing power? You can easily add additional servers to your account, talk to sales for more information.</p>
            </div>
            
            <div className="free-version-cta">
              <p>Not sure which option is right for you? <span className="highlight-text">Start with our trial or free web version</span></p>
              <div className="cta-buttons-container">
                <a href="https://pay.translatestocks.com/b/6oEcPM0vD65wcMg3cW" className="btn btn-primary btn-medium">Try for $150</a>
                <span className="or-divider">or</span>
                <a href="https://www.translatestocks.com/signup" className="btn btn-outline btn-medium">Sign Up Free</a>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Let's add some CSS for the table layout */}
      <style>
        {`
         .pricing-hero {
           background-color: #f8fafc;
           padding: 70px 0 50px;
           text-align: center;
         }
         
         .pricing-hero h1 {
           font-size: 42px;
           font-weight: 800;
           color: #1e293b;
           margin-bottom: 12px;
           letter-spacing: -0.5px;
         }
         
         .pricing-subheadline {
           font-size: 28px;
           font-weight: 600;
           color: #1e293b;
           margin-bottom: 20px;
         }
         
         .pricing-subheadline .highlight {
           color: #2563eb;
         }
         
         .pricing-description {
           font-size: 20px;
           color: #64748b;
           max-width: 680px;
           margin: 0 auto 30px;
           line-height: 1.6;
         }
         
         .key-advantages {
           display: flex;
           justify-content: center;
           gap: 20px;
           margin: 30px 0;
         }
         
         .advantage-pill {
           display: flex;
           align-items: center;
           gap: 10px;
           background-color: white;
           border-radius: 100px;
           padding: 12px 20px;
           box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
           font-weight: 600;
           font-size: 16px;
           color: #1e293b;
           transition: transform 0.2s ease, box-shadow 0.2s ease;
         }
         
         .advantage-pill:hover {
           transform: translateY(-2px);
           box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
         }
         
         .advantage-icon {
           width: 24px;
           height: 24px;
           border-radius: 50%;
           background: rgba(37, 99, 235, 0.1);
           display: flex;
           align-items: center;
           justify-content: center;
         }
         
         .advantage-icon svg {
           width: 16px;
           height: 16px;
         }
         
         /* Pricing Table Improvements */
         .pricing-table-container {
           margin: 40px 0;
         }
         
         .pricing-table {
           border-radius: 12px;
           overflow: hidden;
           box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
           background: white;
         }
         
         .pricing-table-header {
           display: grid;
           grid-template-columns: 2fr 1fr 1fr 1fr;
           border-bottom: 1px solid #eaeaea;
         }
         
         .pricing-table-body {
           display: grid;
         }

         .pricing-plans {
           padding: 60px 0;
         }
         
         .plan-column {
           padding: 30px 25px;
           display: flex;
           flex-direction: column;
           justify-content: space-between;
           align-items: center;
           position: relative;
         }
         
         .plan-column.popular {
           background-color: #f0f7ff;
           position: relative;
           z-index: 1;
           transform: scale(1.05);
           box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
           border-radius: 12px;
         }
         
         .plan-name {
           font-size: 22px;
           font-weight: 700;
           margin-bottom: 10px;
         }
         
         .recommended-for {
           font-size: 16px;
           color: #64748b;
           margin-bottom: 20px;
           background-color: #f3f4f6;
           padding: 4px 10px;
           border-radius: 100px;
         }
         
         .plan-price {
           font-size: 40px;
           font-weight: 800;
           color: #1e293b;
           margin-bottom: 5px;
         }
         
         .cents {
           font-size: 0.5em;
           vertical-align: super;
           font-weight: 600;
         }
         
         .plan-billing {
           font-size: 16px;
           color: #64748b;
           margin-bottom: 15px;
         }
         
         .plan-savings {
           font-size: 16px;
           font-weight: 600;
           color: #10b981;
           margin-bottom: 5px;
         }
         
         .limited-availability {
           background-color: #fff0e5;
           border-radius: 8px;
           padding: 10px;
           margin: 15px 0;
           font-weight: 700;
           color: #f97316; /* Orange color for urgency */
           font-size: 16px;
           border: 1px dashed #f97316;
         }
         
         .counter-number {
           color: #f97316;
           font-weight: 800;
         }
         
         .plan-benefits {
           margin: 25px 0;
           text-align: left;
         }
         
         .benefit-item {
           padding: 8px 0;
           position: relative;
           padding-left: 28px;
           font-size: 16px;
           color: #1e293b;
         }
         
         .benefit-item:before {
           content: "✓";
           position: absolute;
           left: 0;
           color: #2563eb;
           font-weight: bold;
         }
         
         .benefit-item.highlight {
           color: #2563eb;
           font-weight: 600;
         }
         
         .btn-primary {
           background-color: #2563eb;
           color: white;
           border: none;
           border-radius: 8px;
           padding: 12px 24px;
           font-size: 16px;
           font-weight: 600;
           cursor: pointer;
           transition: all 0.2s ease;
           display: inline-block;
           text-decoration: none;
           box-shadow: 0 4px 12px rgba(37, 99, 235, 0.25);
         }
         
         .btn-primary:hover {
           background-color: #1d4ed8;
           transform: translateY(-2px);
           box-shadow: 0 6px 16px rgba(37, 99, 235, 0.3);
         }
         
         .btn-primary.disabled {
           background-color: #94a3b8;
           cursor: not-allowed;
           box-shadow: none;
           opacity: 0.7;
         }
         
         .btn-primary.disabled:hover {
           background-color: #94a3b8;
           transform: none;
           box-shadow: none;
         }
         
         /* Feature Row Improvements */
         .feature-row {
           display: grid;
           grid-template-columns: 2fr 1fr 1fr 1fr;
           border-bottom: 1px solid #e2e8f0;
           align-items: center;
         }
         
         .feature-name {
           padding: 15px;
           font-size: 16px;
           flex: 1;
         }
         
         .feature-row.section-title .feature-name {
           font-weight: 700;
           color: #1e293b;
           background-color: #f8fafc;
         }
         
         .price-rails-link {
           color: #2563eb;
           font-weight: 700;
           display: flex;
           align-items: center;
           gap: 5px;
           text-decoration: none;
         }
         
         .price-rails-link svg {
           width: 14px;
           height: 14px;
         }
         
         .flagship-description {
           display: block;
           font-size: 14px;
           color: #64748b;
           margin-top: 5px;
         }
         
         .flagship-checkmark, .checkmark {
           color: #2563eb;
           font-size: 20px;
           font-weight: bold;
           display: flex;
           justify-content: center;
           align-items: center;
         }
         
         .no-mark {
           color: #9ca3af;
           font-size: 20px;
           display: flex;
           justify-content: center;
           align-items: center;
         }
         
         /* Plan column cell improvements */
         .plan-column.monthly, 
         .plan-column.yearly, 
         .plan-column.lifetime {
           display: flex;
           justify-content: center;
           align-items: center;
           text-align: center;
         }
         
         .feature-row .plan-column {
           padding: 12px;
         }
         
         /* Mobile Plan Selector - hidden by default on desktop */
         .mobile-plan-selector {
           display: none;
         }
         
         /* Mobile Price Display - hidden by default on desktop */
         .mobile-price-display {
           display: none;
         }
         
         .mobile-plan-selector button {
           background: #f3f4f6;
           border: 1px solid #e5e7eb;
           padding: 12px 16px;
           border-radius: 8px;
           font-weight: 600;
           cursor: pointer;
           flex: 1;
           font-size: 16px;
           color: #4b5563;
           transition: all 0.2s ease;
           max-width: 120px;
           box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
         }

         .mobile-plan-selector button:hover {
           background: #e5e7eb;
         }

         .mobile-plan-selector button.active {
           background: #2563eb;
           color: white;
           border-color: #2563eb;
           box-shadow: 0 4px 8px rgba(37, 99, 235, 0.2);
         }
         
         /* Responsive Adjustments */
         @media (max-width: 768px) {
           .pricing-hero h1 {
             font-size: 36px;
           }
           
           .pricing-subheadline {
             font-size: 24px;
           }
           
           .pricing-description {
             font-size: 18px;
             padding: 0 20px;
           }
           
           .key-advantages {
             flex-direction: column;
             align-items: center;
             gap: 15px;
           }

           /* Show mobile pricing elements */
           .mobile-plan-selector {
             display: flex;
             justify-content: center;
             gap: 10px;
             margin-bottom: 15px;
             padding: 0 15px;
           }
           
           .mobile-price-display {
             display: block;
             margin-bottom: 30px;
             padding: 0 15px;
           }
           
           .mobile-price-card {
             background: white;
             border-radius: 12px;
             padding: 25px 20px;
             box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
             text-align: center;
             position: relative;
             overflow: hidden;
           }
           
           .mobile-price-card.popular {
             background: linear-gradient(to bottom, #f0f7ff, white);
             border: 2px solid #2563eb;
           }
           
           .mobile-popular-tag {
             background: #2563eb;
             color: white;
             font-size: 14px;
             font-weight: 700;
             padding: 5px 15px;
             position: absolute;
             top: 0;
             left: 0;
             right: 0;
             text-transform: uppercase;
           }
           
           .mobile-plan-name {
             font-size: 24px;
             font-weight: 700;
             color: #1e293b;
             margin-top: 15px;
             margin-bottom: 5px;
           }
           
           .mobile-price-card.popular .mobile-plan-name {
             margin-top: 25px;
             color: #2563eb;
           }
           
           .mobile-plan-price {
             font-size: 42px;
             font-weight: 800;
             color: #1e293b;
             margin: 15px 0 5px;
           }
           
           .mobile-plan-price .cents {
             font-size: 18px;
             vertical-align: super;
             font-weight: 600;
           }
           
           .mobile-plan-billing {
             font-size: 16px;
             color: #64748b;
             margin-bottom: 10px;
           }
           
           .mobile-plan-savings {
             font-size: 16px;
             font-weight: 600;
             color: #10b981;
             margin: 5px 0 15px;
           }
           
           .mobile-limited-availability {
             background-color: #fff0e5;
             border-radius: 8px;
             padding: 8px 15px;
             margin: 10px 0 15px;
             display: inline-block;
             font-weight: 700;
             color: #f97316;
             border: 1px dashed #f97316;
           }
           
           .btn-mobile {
             display: block;
             width: 100%;
             padding: 14px;
             margin-top: 15px;
             font-size: 16px;
             font-weight: 600;
           }

           /* Pricing table mobile layout */
           .pricing-table-header {
             display: none;
           }
           
           .pricing-table-body .feature-row {
             display: flex;
             flex-direction: column;
             border-bottom: 1px solid #e5e7eb;
             padding: 15px;
           }
           
           .pricing-table-body .feature-row:last-child {
             border-bottom: none;
           }
           
           .pricing-table-body .feature-row.section-title {
             background-color: #f8fafc;
             padding: 12px 15px;
             text-align: center;
             font-weight: 700;
           }
           
           .pricing-table-body .feature-row .feature-name {
             padding: 0 0 10px 0;
             width: 100%;
             border-bottom: none;
           }
           
           .pricing-table-body .flagship-feature {
             background-color: #f0f7ff;
             border-radius: 8px;
             margin-bottom: 15px;
           }
           
           .plan-column {
             display: none;
           }
           
           .feature-row .plan-column.mobile-active {
             display: flex;
             justify-content: center;
             align-items: center;
             margin-top: 8px;
           }
           
           .checkmark, .flagship-checkmark {
             color: #2563eb;
             font-size: 18px;
             font-weight: bold;
           }
           
           .no-mark {
             color: #9ca3af;
             font-size: 18px;
           }
           
           /* FAQ Section Responsive Styles */
           .faq-grid {
             grid-template-columns: 1fr;
             padding: 0 20px;
           }
           
           .faq-header h2 {
             font-size: 28px;
             padding: 0 15px;
           }
           
           .faq-header p {
             font-size: 16px;
             padding: 0 15px;
           }
           
           .faq-item {
             padding: 20px;
           }
           
           .faq-item h3 {
             font-size: 18px;
           }
           
           .faq-item p {
             font-size: 15px;
           }
         }
         
         @media (max-width: 480px) {
           .faq-header h2 {
             font-size: 24px;
           }
           
           .faq-grid {
             gap: 20px;
           }
           
           .faq-item {
             padding: 15px;
           }
         }

         /* FAQ Section Styles */
         .faq-section {
           padding: 80px 0;
           background-color: #f9fafb;
         }
         
         .faq-header {
           text-align: center;
           margin-bottom: 40px;
         }
         
         .faq-header h2 {
           font-size: 36px;
           font-weight: 800;
           color: #1e293b;
           margin-bottom: 12px;
         }
         
         .faq-header p {
           font-size: 18px;
           color: #64748b;
           max-width: 700px;
           margin: 0 auto;
         }
         
         .faq-grid {
           display: grid;
           grid-template-columns: repeat(2, 1fr);
           gap: 30px;
           max-width: 1200px;
           margin: 0 auto;
         }
         
         .faq-item {
           background: white;
           border-radius: 10px;
           padding: 25px;
           box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
           transition: transform 0.2s ease, box-shadow 0.2s ease;
         }
         
         .faq-item:hover {
           transform: translateY(-3px);
           box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
         }
         
         .faq-item h3 {
           font-size: 20px;
           font-weight: 700;
           color: #1e293b;
           margin-bottom: 15px;
         }
         
         .faq-item p {
           font-size: 16px;
           line-height: 1.6;
           color: #64748b;
         }

         /* Contact Section and CTA Responsive Styles */
         .contact-options {
           padding: 60px 0;
         }

         .booking-header h2 {
           font-size: 36px;
           font-weight: 800;
           margin-bottom: 15px;
         }

         .booking-header p {
           font-size: 18px;
           max-width: 700px;
           margin: 0 auto 40px;
         }

         .pricing-cta {
           background-color: #2563eb;
           padding: 70px 0;
           color: white;
           text-align: center;
         }

         /* Trial Policy Card Styles */
         .trial-policy-card {
           background-color: white;
           border-radius: 12px;
           padding: 30px;
           margin: 30px 0;
           box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
           text-align: center;
         }
         
         .trial-policy-card h3 {
           font-size: 24px;
           font-weight: 700;
           color: #1e293b;
           margin-bottom: 25px;
         }
         
         .trial-banner {
           display: flex;
           align-items: center;
           justify-content: center;
           background-color: #f0f7ff;
           border-radius: 10px;
           padding: 20px;
           margin-bottom: 25px;
         }
         
         .trial-banner-icon {
           width: 40px;
           height: 40px;
           margin-right: 15px;
         }
         
         .trial-banner-icon svg {
           width: 100%;
           height: 100%;
         }
         
         .trial-banner-text {
           text-align: left;
         }
         
         .trial-highlight {
           display: block;
           font-size: 20px;
           font-weight: 700;
           color: #2563eb;
           margin-bottom: 5px;
         }
         
         .trial-banner-text p {
           margin: 0;
           font-size: 16px;
           color: #4b5563;
         }
         
         .trial-benefits {
           display: flex;
           flex-direction: column;
           gap: 12px;
           margin: 20px 0 30px;
         }
         
         .trial-benefit-item {
           display: flex;
           align-items: center;
           gap: 10px;
           font-size: 16px;
           color: #1e293b;
         }
         
         .benefit-icon {
           color: #2563eb;
           font-size: 18px;
           font-weight: bold;
         }
         
         .trial-cta {
           display: flex;
           flex-direction: column;
           align-items: center;
           gap: 15px;
         }
         
         .view-policy-link {
           display: inline-block;
           margin-top: 5px;
           color: #2563eb;
           font-size: 14px;
           font-weight: 600;
           text-decoration: none;
           transition: color 0.2s ease;
         }
         
         .view-policy-link:hover {
           color: #1d4ed8;
           text-decoration: underline;
         }
         
         @media (max-width: 768px) {
           .trial-banner {
             flex-direction: column;
             text-align: center;
             padding: 15px;
           }
           
           .trial-banner-icon {
             margin-right: 0;
             margin-bottom: 10px;
           }
           
           .trial-banner-text {
             text-align: center;
           }
           
           .trial-benefit-item {
             font-size: 14px;
           }
         }

         .cta-content h2 {
           font-size: 36px;
           font-weight: 800;
           margin-bottom: 15px;
           color: white;
         }

         .cta-buttons {
           display: flex;
           justify-content: center;
           gap: 20px;
           margin-top: 30px;
         }

         .btn-secondary {
           background-color: white;
           color: #2563eb;
           border: none;
           border-radius: 8px;
           padding: 12px 24px;
           font-size: 16px;
           font-weight: 600;
           cursor: pointer;
           transition: all 0.2s ease;
           display: inline-block;
           text-decoration: none;
           box-shadow: 0 4px 12px rgba(255, 255, 255, 0.2);
         }

         .btn-secondary:hover {
           background-color: #f8fafc;
           transform: translateY(-2px);
         }
         
         /* Responsive Adjustments */
         @media (max-width: 992px) {
           .faq-grid {
             grid-template-columns: 1fr;
             padding: 0 20px;
           }
           
           .cta-buttons {
             flex-direction: column;
             align-items: center;
             gap: 15px;
           }
         }
         
         @media (max-width: 768px) {
           .pricing-hero h1 {
             font-size: 36px;
           }
           
           .pricing-subheadline {
             font-size: 24px;
           }
           
           .pricing-description {
             font-size: 18px;
             padding: 0 20px;
           }
           
           .key-advantages {
             flex-direction: column;
             align-items: center;
             gap: 15px;
           }
           
           .plan-column {
             padding: 25px 15px;
           }
           
           .plan-name {
             font-size: 20px;
           }
           
           .plan-price {
             font-size: 32px;
           }
           
           /* Show mobile plan selector */
           .mobile-plan-selector {
             display: flex;
           }
           
           /* Hide non-active plan columns on mobile */
           .pricing-table-header .plan-column.monthly, 
           .pricing-table-header .plan-column.yearly, 
           .pricing-table-header .plan-column.lifetime,
           .feature-row .plan-column.monthly,
           .feature-row .plan-column.yearly,
           .feature-row .plan-column.lifetime {
             display: none;
           }
           
           /* Contact and CTA Responsive Styles */
           .booking-header h2 {
             font-size: 28px;
             padding: 0 15px;
           }
           
           .booking-header p {
             font-size: 16px;
             padding: 0 15px;
           }
           
           .cta-content h2 {
             font-size: 28px;
             padding: 0 15px;
           }
           
           .cta-content p {
             padding: 0 15px;
           }
           
           /* FAQ Section Responsive Styles */
           .faq-section {
             padding: 50px 0;
           }
           
           .faq-grid {
             grid-template-columns: 1fr !important;
             gap: 20px !important;
             padding: 0 20px !important;
             max-width: 100% !important;
           }
           
           .faq-header h2 {
             font-size: 28px;
             padding: 0 15px;
           }
           
           .faq-header p {
             font-size: 16px;
             padding: 0 15px;
           }
           
           .faq-item {
             padding: 20px;
             margin: 0 10px;
           }
           
           .faq-item h3 {
             font-size: 18px;
           }
           
           .faq-item p {
             font-size: 15px;
           }
         }
         
         @media (max-width: 480px) {
           .faq-section {
             padding: 40px 0;
           }
           
           .faq-header h2 {
             font-size: 24px;
           }
           
           .faq-grid {
             gap: 15px !important;
           }
           
           .faq-item {
             padding: 15px;
           }
           
           .booking-header h2, 
           .cta-content h2 {
             font-size: 24px;
           }
           
           .btn-primary, 
           .btn-secondary, 
           .btn-booking {
             width: 100%;
             max-width: 250px;
           }
         }

         .free-version-cta {
           text-align: center;
           margin-top: 30px;
           padding: 30px;
           background-color: #f8fafc;
           border-radius: 12px;
         }
         
         .free-version-cta p {
           font-size: 18px;
           margin-bottom: 20px;
         }
         
         .highlight-text {
           color: #2563eb;
           font-weight: 600;
         }
         
         .cta-buttons-container {
           display: flex;
           align-items: center;
           justify-content: center;
           gap: 15px;
         }
         
         .or-divider {
           font-size: 16px;
           color: #64748b;
           font-weight: 500;
         }
         
         .btn-outline {
           background-color: transparent;
           color: #2563eb;
           border: 2px solid #2563eb;
           transition: all 0.2s ease;
         }
         
         .btn-outline:hover {
           background-color: #f0f7ff;
           transform: translateY(-2px);
         }
         
         .btn-medium {
           padding: 10px 20px;
           font-size: 16px;
         }
         
         @media (max-width: 768px) {
           .cta-buttons-container {
             flex-direction: column;
             gap: 10px;
           }
           
           .or-divider {
             margin: 5px 0;
           }
         }

         /* Trial Button Styles */
         .btn-trial {
           background-color: #2563eb;
           color: white;
           border: none;
           border-radius: 100px;
           padding: 12px 24px;
           font-size: 16px;
           font-weight: 600;
           cursor: pointer;
           transition: all 0.3s ease;
           display: flex;
           align-items: center;
           justify-content: center;
           gap: 8px;
           margin: 25px auto 0;
           box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3);
         }
         
         .btn-trial:hover {
           background-color: #1d4ed8;
           transform: translateY(-2px);
           box-shadow: 0 6px 16px rgba(37, 99, 235, 0.4);
         }
         
         .btn-trial svg {
           transition: transform 0.2s ease;
         }
         
         .btn-trial:hover svg {
           transform: translateX(3px);
         }
         
         @media (max-width: 768px) {
           .btn-trial {
             width: 100%;
             max-width: 250px;
           }
         }
        `}
      </style>

      
      <section className="contact-options meeting-focus">
        <div className="container">
          <div className="booking-header">
            <h2>Schedule a <span className="highlight">Personal Consultation</span></h2>
            <p>Speak directly with our expert team to explore how Price on Rails can transform your trading strategy</p>
          </div>
          <div className="booking-card">
            <div className="booking-card-content">
              <div className="booking-icon">
                <svg width="64" height="64" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M36.6 30.52C35.1 29.02 33.28 29.02 31.8 30.52L28.68 33.64C26.1 32.32 23.7 30.42 21.56 28.26C19.58 26.28 17.68 23.9 16.36 21.32L19.48 18.2C20.98 16.72 20.98 14.9 19.48 13.4L13.4 7.32C11.92 5.84 10.1 5.84 8.6 7.32L5.08 10.86C3.68 12.26 3.12 14.2 3.48 16.08C4.32 20.52 6.94 26.14 11.4 30.6C15.86 35.06 21.48 37.68 25.92 38.52C26.36 38.6 26.8 38.64 27.22 38.64C28.78 38.64 30.26 38.04 31.38 36.92L34.9 33.4C36.38 31.9 36.38 30.08 34.9 28.6L36.6 30.52Z" fill="#2563EB"/>
                </svg>
              </div>
              <h3>Personalized Video Call</h3>
              <div className="booking-features">
                <div className="feature-item">
                  <span className="feature-icon">✓</span>
                  <span>One-on-one consultation</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">✓</span>
                  <span>Live platform demonstration</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">✓</span>
                  <span>30-minute dedicated session</span>
                </div>
              </div>
              <div className="booking-cta">
                <a href="https://calendly.com/kevin-translatestocks/30min" className="btn-booking">
                  Schedule Your Call
                </a>
                <p className="booking-note">Choose a time that works for you</p>
              </div>
            </div>
          </div>
        </div>
      </section>
            
      <section className="pricing-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Transform Your Trading?</h2>
            <p><span style={{ color: 'white' }}>Join thousands of successful traders using Price on Rails to gain a competitive edge in the market.</span></p>
            <div className="cta-buttons">
              <a href="https://www.translatestocks.com/signup" className="btn btn-primary">Get Started Now</a>
              <Link to="/demo" className="btn btn-secondary">Watch Demo</Link>
            </div>
          </div>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section className="faq-section">
        <div className="container">
          <div className="faq-header">
            <h2>Frequently Asked Questions</h2>
            <p>Everything you need to know about Price on Rails by Translate Stocks</p>
          </div>
          <div className="faq-grid">
            <div className="faq-item">
              <h3>Why is Price on Rails more expensive than other platforms?</h3>
              <p>Price on Rails isn't just software, it's a complete trading advantage system. The price reflects our significant investment in proprietary solutions (Price on Rails is a provision-patented system), a dedicated multi-server infrastructure for EACH user, and white-glove support. Our users justify the investment through their consistently superior returns compared to other platforms.</p>
            </div>

            <div className="faq-item">
              <h3>How does our 78-83% win rate compare to market averages?</h3>
              <p>The average trading system typically achieves a 50-60% win rate, while good systems reach 60-70%. Our 78-83% win rate places us significantly above industry standards. This advantage is achieved through proprietary statistical models and algorithmic analysis that identify high-probability setups most systems miss. And the best part? It's a tool, not a signal. You can use it with your preferred securities and time frames, executing your own trades without competition from other traders. Exclusively yours!</p>
            </div>

            <div className="faq-item">
              <h3>What does "dedicated multi-server infrastructure" mean?</h3>
              <p>Unlike other platforms that run multiple clients on shared servers or require you to download and install software on your computer, each Price on Rails customer gets access to dedicated processing power across multiple specialized servers in a private and isolated network behind a dedicated gateway in New York. This ensures ultra-low latency execution, eliminates resource competition during high-volume periods, and provides 99.99% guaranteed uptime. Yet, you can still access and use it as a desktop platform from any operating system, including Windows, macOS, Linux, Android, and iOS.</p>
            </div>

            <div className="faq-item">
              <h3>Why is the Lifetime tier limited to only {availableLifetimeSeats} seats?</h3>
              <p>Our lifetime offering provides unlimited access to current and future platform features. We limit lifetime seats to ensure each client receives exceptional service, including VIP concierge support and personalized optimization. Additionally, our infrastructure is provisioned to accommodate a specific number of lifetime users without compromising performance.</p>
            </div>

            <div className="faq-item">
              <h3>Can I try the platform before committing to a full subscription?</h3>
              <p>Yes! We offer a 15-day trial for just $150, which gives you access to the core platform features with some limitations. During the trial, broker connections are limited to paper trading accounts only, so the AI Trader Agent Bot will only perform simulated trades. You can still use the platform's signals for manual trading. If you decide to purchase a full plan within 7 days after your trial ends, your trial fee will be applied as credit toward your purchase. This allows you to experience the platform's capabilities before making a larger investment. Please note that only one trial is allowed per person.</p>
            </div>
          </div>
        </div>
      </section>
      
    </div>
  );
};

export default PricingPage;